import type { AxiosPromise } from "axios";
import type {
  FlexibleMode,
  WorkType
} from "./work-policy-api.types";
import type {
  CalendarDate,
  ClockTime,
  WorkPolicyApplicationPayload,
  WorkPolicyApplicationScheduleRow
} from "../components/approval/approval-form/work-policy/work-policy-application.types";

export type WorkPolicyApplicationStage =
  | "NONE"
  | "MASTER_SAVED"
  | "POLICY_BOUND"
  | "TYPE_DETAIL_SAVED"
  | "PARTICIPANTS_SAVED"
  | "FILES_SAVED"
  | "RECONCILED";

export interface WorkPolicyApplicationValidationIssue {
  path: string;
  message: string;
}

export interface WorkPolicyApplicationSuccessEnvelope<T> {
  ok: true;
  data: T;
  resourceId?: number;
  lastCompletedStage?: WorkPolicyApplicationStage;
  retryable: false;
  fingerprint?: string;
}

export interface WorkPolicyApplicationFailureEnvelope {
  ok: false;
  code: string;
  message: string;
  resourceId?: number;
  lastCompletedStage?: WorkPolicyApplicationStage;
  retryable: boolean;
  fingerprint?: string;
  issues?: WorkPolicyApplicationValidationIssue[];
}

interface EligibleWorkPolicyFields {
  workPolicyId: number;
  profileName: string;
  dailyBaseHours: number;
  weeklyBaseHours: number;
  weeklyOvertimeLimit: number;
  breakCalculationEnabled: boolean;
  breakWorkMinutes: number;
  breakMinutes: number;
  selectiveCoreEnabled: boolean;
  selectiveCoreStart: ClockTime | null;
  selectiveCoreEnd: ClockTime | null;
  nightStart: ClockTime;
  nightEnd: ClockTime;
  exposureStatus: "VISIBLE";
  targetType: "all" | "dept" | "user";
}

export interface RawEligibleWorkPolicy extends EligibleWorkPolicyFields {
  workType: WorkType;
  flexibleMode: FlexibleMode | null;
}

export interface EligibleStaggeredWorkPolicy extends EligibleWorkPolicyFields {
  workType: "staggered";
  flexibleMode: null;
}

export interface EligibleFlexibleWorkPolicy extends EligibleWorkPolicyFields {
  workType: "flexible";
  flexibleMode: FlexibleMode;
}

export interface EligibleSelectiveWorkPolicy extends EligibleWorkPolicyFields {
  workType: "selective";
  flexibleMode: null;
}

export type EligibleWorkPolicy =
  | EligibleStaggeredWorkPolicy
  | EligibleFlexibleWorkPolicy
  | EligibleSelectiveWorkPolicy;

function isClock(value: unknown): value is ClockTime {
  if (typeof value !== "string") return false;
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  return Boolean(
    match &&
      Number(match[1]) <= 23 &&
      Number(match[2]) <= 59 &&
      (match[3] === undefined || Number(match[3]) <= 59)
  );
}

function hasEligibleIdentity(policy: RawEligibleWorkPolicy) {
  return (
    Number.isInteger(policy.workPolicyId) &&
    policy.workPolicyId > 0 &&
    typeof policy.profileName === "string" &&
    policy.profileName.trim().length > 0 &&
    policy.exposureStatus === "VISIBLE" &&
    (policy.targetType === "all" ||
      policy.targetType === "dept" ||
      policy.targetType === "user")
  );
}

export function isSupportedEligiblePolicy(
  policy: RawEligibleWorkPolicy
): policy is EligibleWorkPolicy {
  if (!hasEligibleIdentity(policy)) return false;
  return (
    (policy.workType === "staggered" && policy.flexibleMode === null) ||
    (policy.workType === "flexible" &&
      (policy.flexibleMode === "DAILY_DATE" ||
        policy.flexibleMode === "WEEKLY_PATTERN")) ||
    (policy.workType === "selective" &&
      policy.flexibleMode === null &&
      (policy.selectiveCoreEnabled
        ? isClock(policy.selectiveCoreStart) && isClock(policy.selectiveCoreEnd)
        : policy.selectiveCoreStart === null && policy.selectiveCoreEnd === null))
  );
}

export interface ApprovalStaggeredTime {
  staggeredTimeId: number;
  startTime: ClockTime;
  expectedEndTime: ClockTime;
  workMinutes: number;
  breakMinutes: number;
}

export interface ApprovalLineUserInput {
  employee_id: string;
  order: number;
  type: "DRAFTER" | "DECIDE_ARBITRARILY" | "AGREEMENT" | "APPROVAL";
}

export interface ApprovalParticipantUserInput {
  employee_id: string;
}

export interface ApprovalParticipantDepartmentInput {
  department_id: number;
}

export interface ApprovalApplicationPayload {
  fileNames: string[];
  fileSizes: number[];
  uuid: string;
  workingTemplateIdx: number;
  startDate: CalendarDate;
  endDate: CalendarDate;
  startTime: ClockTime;
  endTime: ClockTime;
  description: string;
  replacementRequestStartDate?: CalendarDate | null;
  replacementRequestEndDate?: CalendarDate | null;
  replacementRequestStartTime?: ClockTime | null;
  replacementRequestEndTime?: ClockTime | null;
  formTemplateIdx: number;
  title: string;
  listOfApprovalLineUser: ApprovalLineUserInput[];
  listOfReceiver: ApprovalParticipantUserInput[];
  listOfReferencedPerson: ApprovalParticipantUserInput[];
  listOfReceiveDepartment: ApprovalParticipantDepartmentInput[];
  listOfReferencedPersonDepartment: ApprovalParticipantDepartmentInput[];
  changeWorkingTime?: string;
}

export interface WorkPolicyApplicationRequest {
  formIdx: 12;
  approval: ApprovalApplicationPayload;
  workPolicyApplication: WorkPolicyApplicationPayload;
}

export interface ApprovalStaggeredDetail {
  approvalRequestIdx: number;
  workPolicyId: number;
  selectedStartTime: ClockTime;
  selectedEndTime: ClockTime;
  selectedWorkMinutes: number;
  selectedBreakMinutes: number;
}

export interface ApprovalFlexibleSchedule
  extends WorkPolicyApplicationScheduleRow {
  scheduleId: number;
  approvalRequestIdx: number;
  workPolicyId: number;
  scheduleMode: FlexibleMode;
  breakMinutes: number;
  expectedEndTime: ClockTime | null;
}

export interface SettlementPeriod {
  settlementNo: number;
  settlementStartDate: CalendarDate;
  settlementEndDate: CalendarDate;
}

interface WorkPolicyApplicationDetailCommon {
  approvalRequestIdx: number;
  employeeIdRequest: string;
  formIdx: 12;
  resultValue: number | null;
  startDate: CalendarDate;
  endDate: CalendarDate;
  workPolicyId: number;
  profileName: string;
  dailyBaseHours: number;
  weeklyBaseHours: number;
  weeklyOvertimeLimit: number;
  breakCalculationEnabled: boolean;
  breakWorkMinutes: number;
  breakMinutes: number;
  selectiveCoreEnabled: boolean;
  selectiveCoreStart: ClockTime | null;
  selectiveCoreEnd: ClockTime | null;
  nightStart: ClockTime;
  nightEnd: ClockTime;
  workPolicySnapshotJson: string;
  settlementPeriods: SettlementPeriod[];
}

export interface StaggeredWorkPolicyApplicationDetail
  extends WorkPolicyApplicationDetailCommon {
  workType: "staggered";
  flexibleMode: null;
  staggered: ApprovalStaggeredDetail;
  flexibleSchedule: ApprovalFlexibleSchedule[];
}

export interface FlexibleWorkPolicyApplicationDetail
  extends WorkPolicyApplicationDetailCommon {
  workType: "flexible";
  flexibleMode: FlexibleMode;
  staggered: null;
  flexibleSchedule: ApprovalFlexibleSchedule[];
}

export interface SelectiveWorkPolicyApplicationDetail
  extends WorkPolicyApplicationDetailCommon {
  workType: "selective";
  flexibleMode: null;
  staggered: null;
  flexibleSchedule: ApprovalFlexibleSchedule[];
}

export type HistoricalSelectiveWorkPolicyApplicationDetail =
  SelectiveWorkPolicyApplicationDetail;

export type EditableWorkPolicyApplicationDetail =
  | StaggeredWorkPolicyApplicationDetail
  | FlexibleWorkPolicyApplicationDetail
  | SelectiveWorkPolicyApplicationDetail;

export type HistoricalWorkPolicyApplicationDetail =
  EditableWorkPolicyApplicationDetail;

export type WorkPolicyApplicationDetail =
  HistoricalWorkPolicyApplicationDetail;

export type WorkPolicyApplicationClientState =
  | {
      kind: "reconcile-before-retry";
      resourceId?: number;
      lastCompletedStage?: WorkPolicyApplicationStage;
      fingerprint?: string;
    }
  | { kind: "unsupported-policy" }
  | { kind: "failed" };

export interface WorkPolicyApplicationApi {
  eligible(): Promise<
    WorkPolicyApplicationSuccessEnvelope<EligibleWorkPolicy[]>
  >;
  staggeredTimes(
    workPolicyId: number
  ): Promise<WorkPolicyApplicationSuccessEnvelope<ApprovalStaggeredTime[]>>;
  settlementPeriods(
    startDate: CalendarDate,
    endDate: CalendarDate
  ): Promise<WorkPolicyApplicationSuccessEnvelope<SettlementPeriod[]>>;
  detail(
    approvalRequestIdx: number
  ): Promise<
    WorkPolicyApplicationSuccessEnvelope<HistoricalWorkPolicyApplicationDetail>
  >;
  createDraft(
    request: WorkPolicyApplicationRequest
  ): Promise<
    WorkPolicyApplicationSuccessEnvelope<EditableWorkPolicyApplicationDetail>
  >;
  updateDraft(
    approvalRequestIdx: number,
    request: WorkPolicyApplicationRequest
  ): Promise<
    WorkPolicyApplicationSuccessEnvelope<EditableWorkPolicyApplicationDetail>
  >;
  createSubmission(
    request: WorkPolicyApplicationRequest
  ): Promise<
    WorkPolicyApplicationSuccessEnvelope<EditableWorkPolicyApplicationDetail>
  >;
  submitDraft(
    approvalRequestIdx: number,
    request: WorkPolicyApplicationRequest
  ): Promise<
    WorkPolicyApplicationSuccessEnvelope<EditableWorkPolicyApplicationDetail>
  >;
  resume(
    approvalRequestIdx: number,
    request: WorkPolicyApplicationRequest
  ): Promise<
    WorkPolicyApplicationSuccessEnvelope<EditableWorkPolicyApplicationDetail>
  >;
}

export type WorkPolicyApplicationAxiosRequest<T> = AxiosPromise<
  WorkPolicyApplicationSuccessEnvelope<T>
>;
